import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  endpoints,
  type CampaignChannelDraft,
  type CampaignResponse,
  type UpdateCampaignRequest,
} from "@/lib/api";
import { ChannelPicker } from "@/components/ChannelPicker";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Lbl } from "@/components/ui/Lbl";
import { Modal } from "@/components/ui/Modal";

// Campaign-level modals: add a KOL to the campaign, and edit the campaign
// itself. v2.4.0 — extracted from CampaignDetail.tsx alongside the other
// two component files; these two stay together because both mutate the
// campaign root rather than any individual campaign_channel.

export function AddChannelModal({
  campaignID,
  onClose,
  onAdded,
}: {
  campaignID: number;
  onClose: () => void;
  onAdded: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CampaignChannelDraft>({
    channel_id: 0,
    referral_code: "",
    kol_uid: "",
    payment_currency: "USD",
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: CampaignChannelDraft) =>
      endpoints.addCampaignChannel(campaignID, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign", campaignID] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      onAdded();
    },
    onError: (err) => setError((err as Error).message),
  });

  // v2.1.9 — when the operator picks a KOL, auto-fill referral_code +
  // kol_uid from the channel's KOL identity fields. The operator can
  // still override; we only fill when the form field is currently empty
  // so we don't clobber what they just typed.
  function pickChannel(id: number | null) {
    if (!id) {
      setForm((f) => ({ ...f, channel_id: 0 }));
      return;
    }
    setForm((f) => ({ ...f, channel_id: id }));
    endpoints.channel(id).then((ch) => {
      setForm((f) => {
        // Only fill if user hasn't typed something + channel has the data.
        const out = { ...f };
        if (!out.referral_code && ch.ReferralCode) {
          out.referral_code = ch.ReferralCode;
        }
        if (!out.kol_uid && ch.KolUid) {
          out.kol_uid = ch.KolUid;
        }
        return out;
      });
    }).catch(() => {
      // Silent — auto-fill is best-effort. Operator can type the fields manually.
    });
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.channel_id) return setError("请选择频道");
    mutation.mutate({
      ...form,
      referral_code: form.referral_code || undefined,
      kol_uid: form.kol_uid || undefined,
      agreed_deliverables: form.agreed_deliverables || undefined,
      payment_currency: form.payment_currency || undefined,
      notes: form.notes || undefined,
      deal_amount: form.deal_amount ? Number(form.deal_amount) : undefined,
    });
  }

  return (
    <Modal
      title="添加 KOL 到投放"
      onClose={onClose}
      onSubmit={submit}
      error={error}
      submitLabel="添加"
      submittingLabel="添加中…"
      submitting={mutation.isPending}
    >
      <div className="grid grid-cols-2 gap-3">
        <Lbl label="频道 *" full>
          <ChannelPicker
            value={form.channel_id || null}
            onChange={(id) => pickChannel(id ?? null)}
          />
        </Lbl>
        <Lbl label="返佣码">
          <Input
            value={form.referral_code ?? ""}
            onChange={(e) => setForm({ ...form, referral_code: e.target.value })}
          />
        </Lbl>
        <Lbl label="KOL UID">
          <Input
            value={form.kol_uid ?? ""}
            onChange={(e) => setForm({ ...form, kol_uid: e.target.value })}
          />
        </Lbl>
        <Lbl label="金额">
          <Input
            type="number"
            step="0.01"
            value={form.deal_amount ?? ""}
            onChange={(e) =>
              setForm({
                ...form,
                deal_amount: e.target.value ? Number(e.target.value) : undefined,
              })
            }
          />
        </Lbl>
        <Lbl label="币种">
          <Input
            value={form.payment_currency ?? ""}
            onChange={(e) => setForm({ ...form, payment_currency: e.target.value })}
          />
        </Lbl>
        <Lbl label="约定交付物" full>
          <Input
            value={form.agreed_deliverables ?? ""}
            onChange={(e) => setForm({ ...form, agreed_deliverables: e.target.value })}
          />
        </Lbl>
      </div>
    </Modal>
  );
}

export function EditCampaignModal({
  campaign,
  onClose,
  onSaved,
}: {
  campaign: CampaignResponse;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: campaign.name,
    status: campaign.status,
    start_date: campaign.start_date ?? "",
    end_date: campaign.end_date ?? "",
    notes: campaign.notes ?? "",
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: UpdateCampaignRequest) =>
      endpoints.updateCampaign(campaign.id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign", campaign.id] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      onSaved();
    },
    onError: (err) => setError((err as Error).message),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) return setError("名称不能为空");
    mutation.mutate({
      name: form.name.trim(),
      status: form.status,
      start_date: form.start_date || undefined,
      end_date: form.end_date || undefined,
      notes: form.notes || undefined,
    });
  }

  return (
    <Modal
      title="编辑活动"
      onClose={onClose}
      onSubmit={submit}
      error={error}
      submitLabel="保存"
      submittingLabel="保存中…"
      submitting={mutation.isPending}
    >
      <div className="grid grid-cols-2 gap-3">
        <Lbl label="名称" full>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            autoFocus
          />
        </Lbl>
        <Lbl label="状态">
          <Select
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
          >
            <option value="planned">计划中</option>
            <option value="active">进行中</option>
            <option value="completed">已完成</option>
            <option value="cancelled">已取消</option>
            <option value="archived">已归档</option>
          </Select>
        </Lbl>
        <Lbl label="开始日期">
          <Input
            type="date"
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
          />
        </Lbl>
        <Lbl label="结束日期">
          <Input
            type="date"
            value={form.end_date}
            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
          />
        </Lbl>
        <Lbl label="备注" full>
          <textarea
            className="h-20 w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </Lbl>
      </div>
    </Modal>
  );
}
